import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { api, AuthUser } from '../lib/api'

interface AuthContextValue {
  user: AuthUser | null
  token: string | null
  loading: boolean
  login: (email: string, password: string) => Promise<AuthUser>
  loginSandboxPersona: (personaKey: string) => Promise<AuthUser>
  logout: () => void
  hasRole: (...roles: string[]) => boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [token, setToken] = useState<string | null>(localStorage.getItem('aux_token'))
  const [loading, setLoading] = useState(true)

  // On mount, verify existing token
  useEffect(() => {
    const stored = localStorage.getItem('aux_token')
    if (!stored) { setLoading(false); return }
    api.auth.me()
      .then(r => { setUser(r.data); setToken(stored) })
      .catch(() => { localStorage.removeItem('aux_token'); localStorage.removeItem('aux_refresh'); setToken(null) })
      .finally(() => setLoading(false))
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.auth.login(email, password)
    const { token: t, refreshToken } = res.data
    localStorage.setItem('aux_token', t)
    if (refreshToken) localStorage.setItem('aux_refresh', refreshToken)
    setToken(t)
    const me = await api.auth.me()
    setUser(me.data)
    return me.data
  }, [])

  const loginSandboxPersona = useCallback(async (personaKey: string) => {
    const res = await api.auth.sandboxLogin(personaKey)
    const { token: t, refreshToken } = res.data
    localStorage.setItem('aux_token', t)
    if (refreshToken) localStorage.setItem('aux_refresh', refreshToken)
    setToken(t)
    const me = await api.auth.sandboxMe()
    setUser(me.data)
    return me.data
  }, [])

  const logout = useCallback(() => {
    const refresh = localStorage.getItem('aux_refresh') ?? undefined
    api.auth.logout(refresh).catch(() => {})
    localStorage.removeItem('aux_token')
    localStorage.removeItem('aux_refresh')
    setToken(null)
    setUser(null)
  }, [])

  const hasRole = useCallback((...roles: string[]) => {
    if (!user) return false
    const normalizedAllowed = new Set(roles.map((role) => String(role).toUpperCase()))
    const effectiveRoles = new Set<string>([
      String(user.accountType || '').toUpperCase(),
      ...((user.roles ?? []).map((role) => String(role).toUpperCase())),
    ])
    for (const role of effectiveRoles) {
      if (normalizedAllowed.has(role)) return true
    }
    return false
  }, [user])

  return (
    <AuthContext.Provider value={{ user, token, loading, login, loginSandboxPersona, logout, hasRole }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
